import { isDefined } from "@clipboard-health/util-ts";
import { Box } from "@mui/material";
import { useSession } from "@src/appV2/Auth/api/useSession";
import { type UseModalState } from "@src/lib/ui-react";
import { type ReactNode } from "react";

import { Divider } from "../components/Divider";
import { ChatHeader } from "./ChatHeader";
import { ChatInput } from "./ChatInput";
import { ChatPanelMessageContent } from "./ChatPanelMessageContent";
import { ChatAdminReadOnly } from "./components/ChatAdminReadOnly";
import { ChatDeleteMessageDialog } from "./components/ChatDeleteMessageDialog";
import { ChatEditPreview } from "./components/ChatEditPreview";
import { ChatReplyPreview } from "./components/ChatReplyPreview";
import { useInviteWorkerToShift } from "./components/InviteToShift/useInviteWorkerToShift";
import { useShareWorkerCredentials } from "./components/ShareCredentials/useShareWorkerCredentials";
import { useChatPanelState } from "./hooks/useChatPanelState";
import { useTu } from "@src/appV2/i18n/useTu";

export interface ChatPanelProps {
  modalState: UseModalState;
  /**
   * Workplace ID for the chat channel. When omitted, defaults to the
   * logged-in user's profile ID from the session.
   */
  workplaceId?: string;
  /**
   * Worker ID for API integration to fetch/send chat messages
   */
  workerId: string;
  workerName: string;
  /**
   * When provided, loads the placement-specific channel (3-part URL).
   */
  placementId?: string;
  /**
   * Optional callback when chat panel is closed
   */
  onClose?: () => void;
  /**
   * Optional callback when "View Profile" is clicked in the chat header.
   * When provided, the parent handles opening the worker profile (e.g. replacing panels).
   * When omitted, defaults to navigating to the worker profile page.
   */
  onViewProfile?: (workerId: string) => void;
  /**
   * Whether to show the "View Profile" button in the chat header.
   * Defaults to true. Set to false when the chat opens on a page that already shows the worker profile.
   */
  showViewProfile?: boolean;
  /**
   * Optional fixed context content rendered below the header and above the
   * scrollable message log.
   */
  contextContent?: ReactNode;
  /**
   * Forwarded to the invite-to-shift dialog's qualification slot. Chat
   * surfaces with channel metadata can pass `metadata.agentReq` so the
   * dialog can default the qualification filter.
   */
  channelQualification?: string;
}

/**
 * Chat panel component for worker communication.
 * Displays chat messages with a worker and provides an input to send new messages.
 * Connected to Sendbird for real-time messaging.
 */
export function ChatPanel(props: ChatPanelProps) {
  const { tu } = useTu("chat");
  const {
    modalState,
    workplaceId: workplaceIdFromCaller,
    workerId,
    workerName,
    placementId,
    onClose,
    onViewProfile,
    showViewProfile,
    contextContent,
    channelQualification,
  } = props;

  const {
    workplaceId,
    isAdmin,
    isWorkerSession,
    isLoading,
    isError,
    isBlocked,
    isSendingDisabled,
    messages,
    hasMore,
    isLoadingMore,
    exclusion,
    replyTo,
    editingMessage,
    deleteModalState,
    isDeleting,
    handleClose,
    handleSend,
    handleFileSelect,
    handleRetry,
    handleMarkMessageAsUnread,
    handleMarkConversationAsUnread,
    canMarkConversationAsUnread,
    loadMore,
    handleReply,
    clearReply,
    handleEdit,
    clearEditing,
    handleDelete,
    handleDeleteConfirm,
    handleDeleteCancel,
  } = useChatPanelState({
    modalState,
    workplaceId: workplaceIdFromCaller,
    workerId,
    placementId,
    onClose,
  });

  const canEditOrDelete = !isAdmin && !isBlocked && !isWorkerSession;

  const session = useSession();
  const facilityTimeZone = session.profile.tmz || undefined;

  const { canInvite, openInviteDialog, inviteDialogElement } = useInviteWorkerToShift({
    workerId,
    workplaceId,
    surfaceDisabled: isDefined(placementId),
    channelQualification,
  });
  const onInviteToShift = canInvite ? openInviteDialog : undefined;

  const { canShareCredentials, openShareCredentialsDialog, shareCredentialsDialogElement } =
    useShareWorkerCredentials({
      workerId,
      workplaceId,
      workerName,
      surfaceDisabled: isDefined(placementId),
    });

  return (
    <Box
      sx={(theme) => ({
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        overflow: "hidden",
        backgroundColor: theme.palette.background.paper,
      })}
    >
      <Box sx={{ flexShrink: 0, padding: 5, paddingBottom: 4 }}>
        <ChatHeader
          workerId={workerId}
          workplaceId={workplaceId}
          workerName={workerName}
          isWorkerSession={isWorkerSession}
          showViewProfile={showViewProfile}
          onClose={handleClose}
          onViewProfile={onViewProfile}
          onMarkConversationAsUnread={
            canMarkConversationAsUnread && !isAdmin && !isBlocked && !isWorkerSession
              ? handleMarkConversationAsUnread
              : undefined
          }
        />
      </Box>

      <Divider />

      {contextContent ? (
        <>
          <Box sx={{ flexShrink: 0, padding: 5, paddingBottom: 0 }}>{contextContent}</Box>
          <Divider sx={{ marginTop: 5 }} />
        </>
      ) : null}

      <Box
        role="log"
        aria-label={tu("Chat messages")}
        aria-live="polite"
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          padding: 5,
          display: "flex",
          flexDirection: "column-reverse",
          gap: 4,
        }}
      >
        <ChatPanelMessageContent
          messages={messages}
          workerName={workerName}
          workplaceId={workplaceId}
          facilityTimeZone={facilityTimeZone}
          isLoading={isLoading}
          isError={isError}
          isBlocked={isBlocked}
          isAdmin={isAdmin}
          hasMore={hasMore}
          isLoadingMore={isLoadingMore}
          exclusion={exclusion}
          onRetry={handleRetry}
          onLoadMore={loadMore}
          onReply={handleReply}
          onMarkAsUnread={
            isAdmin || isBlocked || isWorkerSession ? undefined : handleMarkMessageAsUnread
          }
          onEdit={canEditOrDelete ? handleEdit : undefined}
          onDelete={canEditOrDelete ? handleDelete : undefined}
        />
      </Box>

      {editingMessage && canEditOrDelete && (
        <ChatEditPreview message={editingMessage.originalText} onCancel={clearEditing} />
      )}

      {replyTo && !editingMessage && !isAdmin && !isBlocked && (
        <ChatReplyPreview
          message={replyTo.message}
          senderName={replyTo.senderName}
          onCancel={clearReply}
        />
      )}

      {!isBlocked && (
        <Box sx={{ flexShrink: 0, padding: 5 }}>
          {isAdmin ? (
            <ChatAdminReadOnly onInviteToShift={onInviteToShift} />
          ) : (
            <ChatInput
              key={editingMessage?.messageId ?? "compose"}
              disabled={isSendingDisabled}
              initialValue={editingMessage?.originalText}
              onFileSelect={editingMessage ? undefined : handleFileSelect}
              onInviteToShift={editingMessage ? undefined : onInviteToShift}
              onShareCredentials={
                editingMessage || !canShareCredentials ? undefined : openShareCredentialsDialog
              }
              onSend={handleSend}
            />
          )}
        </Box>
      )}

      <ChatDeleteMessageDialog
        isDeleting={isDeleting}
        modalState={deleteModalState}
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
      />

      {inviteDialogElement}
      {shareCredentialsDialogElement}
    </Box>
  );
}
